import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import {
  COLUMN_DESCRIPTION_MAX_LENGTH,
  COLUMN_TITLE_MAX_LENGTH,
  MAX_TEMPLATE_COLUMNS,
  TEMPLATE_DESCRIPTION_MAX_LENGTH,
  TEMPLATE_NAME_MAX_LENGTH,
  type TemplateColumnSeed,
} from '@retro/shared';
import { ApiError, api, type TemplateDraft } from '../lib/api.js';
import { useCurrentUser } from '../lib/auth.js';
import { toast } from '../lib/toast.js';
import { columnAccent } from '../lib/colors.js';
import { AppShell } from '../components/AppShell.js';
import { EmojiPicker } from '../components/EmojiPicker.js';
import { Modal, PageHead, Spinner } from '../components/ui.js';

const emptyColumn = (): TemplateColumnSeed => ({ title: '', description: '', emoji: '' });

/**
 * Author or edit a team's own retrospective format.
 *
 * Reordering uses buttons rather than drag-and-drop: it is a short list, and
 * buttons work with a keyboard and on a phone without any of the drag machinery
 * the board needs.
 */
export function TemplateBuilderPage() {
  const { teamId, templateId } = useParams<{ teamId: string; templateId?: string }>();
  const [searchParams] = useSearchParams();
  const duplicateOf = searchParams.get('from');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  const templatesQuery = useQuery({
    queryKey: ['templates', teamId],
    queryFn: () => api.templates.list(teamId!),
    enabled: Boolean(teamId),
  });

  const isEditing = Boolean(templateId);
  const source = useMemo(() => {
    const templates = templatesQuery.data?.templates ?? [];
    return templates.find((template) => template.id === (templateId ?? duplicateOf)) ?? null;
  }, [templatesQuery.data, templateId, duplicateOf]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [columns, setColumns] = useState<TemplateColumnSeed[]>([emptyColumn()]);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate once the source template arrives; `loaded` stops a refetch from
  // wiping out edits in progress.
  useEffect(() => {
    if (loaded) return;

    if (!templateId && !duplicateOf) {
      setLoaded(true);
      return;
    }
    if (!source) return;

    setName(isEditing ? source.name : `${source.name} (copy)`);
    setDescription(source.description);
    setColumns(source.columns.length > 0 ? source.columns.map((column) => ({ ...column })) : [emptyColumn()]);
    setLoaded(true);
  }, [source, templateId, duplicateOf, isEditing, loaded]);

  const back = `/teams/${teamId}/formats`;

  function patchColumn(index: number, patch: Partial<TemplateColumnSeed>) {
    setDirty(true);
    setColumns((current) =>
      current.map((column, i) => (i === index ? { ...column, ...patch } : column)),
    );
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= columns.length) return;
    setDirty(true);
    setColumns((current) => {
      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      return next;
    });
  }

  const save = useMutation({
    mutationFn: (draft: TemplateDraft) =>
      isEditing ? api.templates.update(templateId!, draft) : api.templates.create({ teamId: teamId!, ...draft }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates', teamId] });
      toast.info(isEditing ? 'Format updated.' : 'Format created.');
      navigate(back);
    },
    onError: (caught: Error) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save that format.'),
  });

  const remove = useMutation({
    mutationFn: () => api.templates.remove(templateId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates', teamId] });
      toast.info('Format deleted.');
      navigate(back);
    },
    onError: (caught: Error) => toast.error(caught.message),
  });

  const trimmedColumns = columns
    .map((column) => ({
      title: column.title.trim(),
      description: column.description.trim(),
      emoji: column.emoji.trim(),
    }))
    .filter((column) => column.title.length > 0);

  const canSave = name.trim().length > 0 && trimmedColumns.length > 0 && !save.isPending;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (!name.trim()) return setError('Give the format a name.');
    if (trimmedColumns.length === 0) return setError('Add at least one column with a title.');

    save.mutate({ name: name.trim(), description: description.trim(), columns: trimmedColumns });
  }

  function leave() {
    if (dirty) return setConfirmDiscard(true);
    navigate(back);
  }

  if (!user || !teamId) return null;

  if (templatesQuery.isLoading || !loaded) {
    return (
      <AppShell user={user} crumb="Format studio">
        <Spinner label="Loading…" />
      </AppShell>
    );
  }

  // Editing something that is not the team's own format, or does not exist.
  if (isEditing && (!source || !source.isCustom)) {
    return (
      <AppShell user={user} crumb="Format studio">
        <div className="card pad mx-auto max-w-xl text-center">
          <p className="text-[13px] text-ink2">
            {source && !source.isCustom
              ? 'Built-in formats cannot be edited. Duplicate it instead to make your own version.'
              : 'That format no longer exists.'}
          </p>
          <Link to={back} className="btn mt-4 inline-flex">
            Back to formats
          </Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell user={user} crumb="Format studio">
      <form onSubmit={onSubmit}>
        <PageHead
          eyebrow="Format studio"
          title={isEditing ? 'Edit format' : duplicateOf ? 'Duplicate format' : 'New format'}
          lede="Team-created formats are only visible to this team, and usable from the next retrospective onwards."
          actions={
            <>
              {isEditing ? (
                <button
                  type="button"
                  className="btn btn-danger-text"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 size={15} aria-hidden /> Delete
                </button>
              ) : null}
              <button type="button" className="btn" onClick={leave}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={!canSave}>
                {save.isPending ? 'Saving…' : 'Save format'}
              </button>
            </>
          }
        />

        {error ? (
          <p role="alert" className="err-msg mb-4">
            {error}
          </p>
        ) : null}

        <section className="card pad stack">
          <label className="field-l">
            Name
            <input
              className="input"
              value={name}
              maxLength={TEMPLATE_NAME_MAX_LENGTH}
              onChange={(event) => {
                setDirty(true);
                setName(event.target.value);
              }}
              placeholder="Muda, Muri, Mura"
              autoFocus
            />
          </label>

          <label className="field-l">
            Description
            <input
              className="input"
              value={description}
              maxLength={TEMPLATE_DESCRIPTION_MAX_LENGTH}
              onChange={(event) => {
                setDirty(true);
                setDescription(event.target.value);
              }}
              placeholder="Look at waste, overburden and unevenness in how we work."
              // Described, not labelled: helper text inside the <label> would be
              // read out as part of the field's name.
              aria-describedby="format-description-hint"
            />
          </label>
          <p id="format-description-hint" className="hint !mt-0">
            Shown under the name when someone picks a format.
          </p>
        </section>

        <section className="card pad mt-4">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h3 className="h3">Columns</h3>
            <span className="text-[12px] text-muted">
              {columns.length} of {MAX_TEMPLATE_COLUMNS}
            </span>
          </div>

          <ol className="col-list">
            {columns.map((column, index) => (
              <li key={index} className="col-ed">
                <span className="bar" style={{ background: columnAccent(index) }} aria-hidden />
                <div className="col-fields">
                  <EmojiPicker
                    value={column.emoji}
                    onChange={(emoji) => patchColumn(index, { emoji })}
                    label={`Icon for column ${index + 1}`}
                  />
                  <div className="grid flex-1 gap-2">
                    <input
                      className="input"
                      value={column.title}
                      maxLength={COLUMN_TITLE_MAX_LENGTH}
                      onChange={(event) => patchColumn(index, { title: event.target.value })}
                      placeholder="Column title"
                      aria-label={`Title for column ${index + 1}`}
                    />
                    <input
                      className="input"
                      value={column.description}
                      maxLength={COLUMN_DESCRIPTION_MAX_LENGTH}
                      onChange={(event) => patchColumn(index, { description: event.target.value })}
                      placeholder="The prompt people answer in this column"
                      aria-label={`Prompt for column ${index + 1}`}
                    />
                  </div>
                </div>

                <div className="col-ctl">
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Move column ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp size={15} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Move column ${index + 1} down`}
                    disabled={index === columns.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown size={15} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Remove column ${index + 1}`}
                    disabled={columns.length === 1}
                    onClick={() => {
                      setDirty(true);
                      setColumns((current) => current.filter((_, i) => i !== index));
                    }}
                  >
                    <X size={15} aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ol>

          <button
            type="button"
            className="btn mt-3"
            disabled={columns.length >= MAX_TEMPLATE_COLUMNS}
            onClick={() => {
              setDirty(true);
              setColumns((current) => [...current, emptyColumn()]);
            }}
          >
            <Plus size={15} aria-hidden /> Add column
          </button>
          {columns.length >= MAX_TEMPLATE_COLUMNS ? (
            <p className="hint mt-2">
              {MAX_TEMPLATE_COLUMNS} columns is the practical maximum — more than that stops being
              readable, especially on a phone.
            </p>
          ) : null}
        </section>

        <section className="mt-4">
          <h3 className="h3 mb-2 px-1">Preview</h3>
          <div className="board thin-scroll" aria-hidden="true">
            {trimmedColumns.length === 0 ? (
              <p className="px-1 py-2 text-[13px] text-muted">
                Give a column a title to see the board preview.
              </p>
            ) : (
              trimmedColumns.map((column, index) => (
                <section key={index} className="col !min-h-0">
                  <header className="col-h" style={{ borderTopColor: columnAccent(index) }}>
                    <h3>
                      {column.emoji ? `${column.emoji} ` : ''}
                      {column.title}
                    </h3>
                    {column.description ? <p>{column.description}</p> : null}
                  </header>
                  <div className="col-b">
                    <span className="note muted">+ Add your idea</span>
                  </div>
                </section>
              ))
            )}
          </div>
        </section>
      </form>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this format?"
        width="sm"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              Delete format
            </button>
          </>
        }
      >
        <p className="text-[13px] text-ink2">
          The team will no longer be able to start new retrospectives with it. Retrospectives already run
          with this format keep their columns and are unaffected.
        </p>
      </Modal>

      <Modal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        title="Discard your changes?"
        width="sm"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setConfirmDiscard(false)}>
              Keep editing
            </button>
            <button type="button" className="btn btn-danger" onClick={() => navigate(back)}>
              Discard
            </button>
          </>
        }
      >
        <p className="text-[13px] text-ink2">This format has unsaved changes.</p>
      </Modal>
    </AppShell>
  );
}
